import React, { useState, useEffect, useMemo } from 'react';
import { uiStateSelector } from 'state/selectors/ui-state';
import { useSelector } from 'react-redux';
import { selectedTeamWithExtraDataSelector } from 'state/selectors/teams';
import useSettingsService from 'components/hooks/useSettingsService';
import { DefinitionLevels, SettingValues } from '@linearb/linjs-common';
import {
  getIncludedMetrics,
  getMetricsNamesToFetch,
  widgetsMetricsNamesInitialState
} from '../../GroupDashboard/groupDashboard.service';
import useGroupMetrics from '../../GroupDashboard/hooks/useGroupMetrics/useGroupMetrics';
import { getAvailableMetrics } from 'common/services/metrics.service';
import GroupMetricsWidgets from '../../GroupDashboard/GroupMetricsWidget/GroupMetricsWidgets';
import { getGranularityByDates } from '../../../performance/views/CustomReport/custom-report.util';
import {
  GRANULARITY,
  GRANULARITY_TO_METRICS_V2_GRANULARITY
} from '../../../performance/views/CustomReport/consts';
import useMetricsCalculationConfig from 'views/root/views/home/hooks/useMetricsCalculationConfig';
import {
  MAX_METRICS_TO_SELECT,
  MIN_METRICS_TO_SELECT,
  METRICS_MODAL_INSTRUCTIONS
} from './MetricsWidgets.service';
import { uniq } from 'lodash';

const MetricsWidgets = ({ toggleModal, gitScopeToggle }) => {
  const uiState = useSelector(uiStateSelector);
  const { dates } = uiState;
  const selectedTeamWithExtraData =
    useSelector(selectedTeamWithExtraDataSelector) || {};
  const { getSettingOrDefinitionValue } = useSettingsService();

  const widgetsMetricsNamesInitialStateSettingValue =
    getSettingOrDefinitionValue({
      name: SettingValues.GROUP_DASHBOARD_METRICS_WIDGETS,
      definitionLevel: DefinitionLevels.TEAM,
      teamId: uiState?.selectedTeamId
    });

  const [widgetsMetricsNames, setWidgetsMetricsNames] = useState(
    widgetsMetricsNamesInitialStateSettingValue ||
      widgetsMetricsNamesInitialState
  );

  useEffect(() => {
    if (uiState?.selectedTeamId) {
      const currentWidgetsSelection = getSettingOrDefinitionValue({
        name: SettingValues.GROUP_DASHBOARD_METRICS_WIDGETS,
        definitionLevel: DefinitionLevels.TEAM,
        teamId: uiState?.selectedTeamId
      });

      setWidgetsMetricsNames(currentWidgetsSelection);
    }
  }, [uiState?.selectedTeamId]);

  const metricsNames = useMemo(
    () => uniq(getMetricsNamesToFetch(widgetsMetricsNames)),
    [widgetsMetricsNames]
  );
  const [availableMetricsWidgets, setAvailableMetricsWidgets] = useState([]);
  const metricsCalculationConfig = useMetricsCalculationConfig();
  const { metricsCalculationStrategy } = metricsCalculationConfig;
  const { isLoading, metrics } = useGroupMetrics({
    metricsNames,
    dates,
    metricsCalculationStrategy,
    selectedGroup: selectedTeamWithExtraData,
    shouldGetOnlySelectedGroupData: true,
    gitScopeToggle
  });
  const granularity = getGranularityByDates({
    start_date: dates.from,
    end_date: dates.to,
    selectedGranularity: GRANULARITY.AUTO
  });
  const {
    isLoading: metricsPerGranularityLoading,
    metrics: metricsPerGranularity
  } = useGroupMetrics({
    metricsNames,
    dates,
    metricsCalculationStrategy,
    selectedGroup: selectedTeamWithExtraData,
    roll_up: GRANULARITY_TO_METRICS_V2_GRANULARITY[granularity],
    shouldGetOnlySelectedGroupData: true,
    gitScopeToggle
  });

  useEffect(() => {
    getAvailableMetrics().then(metrics => {
      const data = metrics ? Object.values(metrics) : [];
      const availableMetricsByType = getIncludedMetrics(data);
      setAvailableMetricsWidgets(availableMetricsByType);
    });
  }, []);

  return (
    <GroupMetricsWidgets
      metrics={metrics}
      selectedGroup={selectedTeamWithExtraData}
      widgetsMetricsNames={widgetsMetricsNames}
      metricsWidgetWidth={'25%'}
      toggleModal={toggleModal}
      availableMetricsWidgets={availableMetricsWidgets}
      setWidgetsMetricsNames={setWidgetsMetricsNames}
      metricsLoading={isLoading}
      metricsPerGranularityLoading={metricsPerGranularityLoading}
      isWidgetsSettingValue={widgetsMetricsNamesInitialStateSettingValue}
      metricsPerGranularity={metricsPerGranularity}
      minMetricsToSelect={MIN_METRICS_TO_SELECT}
      maxMetricsToSelect={MAX_METRICS_TO_SELECT}
      modalInstructions={METRICS_MODAL_INSTRUCTIONS}
    />
  );
};

export default MetricsWidgets;
